import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { BidHistoryItem, BidHistoryFilters, BidOutcome } from '@/types/bidHistory';
import { subDays, startOfDay, endOfDay } from 'date-fns';

const DEFAULT_FILTERS: BidHistoryFilters = {
  dateRange: {
    start: subDays(new Date(), 90),
    end: new Date(),
  },
  outcome: 'all',
  category: 'all',
  search: '',
};

interface UseBidHistoryResult {
  bids: BidHistoryItem[];
  loading: boolean;
  error: string | null;
  filters: BidHistoryFilters;
  setFilters: (filters: BidHistoryFilters) => void;
  refetch: () => void;
}

export function useBidHistory(): UseBidHistoryResult {
  const { user } = useAuth();
  const [bids, setBids] = useState<BidHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BidHistoryFilters>(DEFAULT_FILTERS);

  const fetchBids = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First get user's organization from profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.organization_id) {
        setBids([]);
        setLoading(false);
        return;
      }

      // Get role via RPC function
      const { data: orgRole } = await supabase
        .rpc('get_user_org_role', {
          _user_id: user.id,
          _org_id: profile.organization_id
        } as any);

      // Get all vendor user IDs in the organization if owner/super_user
      let vendorUserIds: string[] = [user.id];
      if (orgRole === 'owner' || orgRole === 'super_user') {
        const { data: orgMembers } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('organization_id', profile.organization_id);
        
        if (orgMembers) {
          vendorUserIds = orgMembers.map(m => m.user_id);
        }
      }

      // Fetch all bids for the vendor(s) within the date range
      const { data: bidsData, error: bidsError } = await supabase
        .from('bids')
        .select(`
          id,
          created_at,
          lead_time_days,
          status,
          vendor_user_id,
          rfq_id,
          rfqs!inner(
            id,
            public_id,
            created_at,
            closes_at,
            job_id,
            job:jobs(
              id,
              title,
              category,
              status,
              organization_id,
              organization:organizations(
                id,
                name
              )
            )
          )
        `)
        .in('vendor_user_id', vendorUserIds)
        .gte('created_at', startOfDay(filters.dateRange.start).toISOString())
        .lte('created_at', endOfDay(filters.dateRange.end).toISOString())
        .order('created_at', { ascending: false });

      if (bidsError) throw bidsError;

      if (!bidsData || bidsData.length === 0) {
        setBids([]);
        setLoading(false);
        return;
      }

      // Get job IDs to fetch awards and feedback
      const jobIds = [...new Set(bidsData.map(b => (b.rfqs as any)?.job?.id).filter(Boolean))];
      const bidIds = bidsData.map(b => b.id);

      // Fetch awards for these jobs using restricted view (excludes sensitive financial data)
      const { data: awardsData } = await supabase
        .from('awards_vendor_view')
        .select('id, job_id, winning_bid_id, awarded_at')
        .in('job_id', jobIds);

      // Fetch award feedback for these jobs
      const { data: feedbackData } = await supabase
        .from('rfq_award_feedback')
        .select('job_id, award_id, selection_reasons, additional_comments')
        .in('job_id', jobIds);

      // Fetch bid line items totals
      const { data: lineItemsData } = await supabase
        .from('bid_line_items')
        .select('bid_id, total_price_cents')
        .in('bid_id', bidIds);

      // Create lookup maps
      const awardsMap = new Map(awardsData?.map(a => [a.job_id, a]) || []);
      const feedbackMap = new Map(feedbackData?.map(f => [f.job_id, f]) || []);
      const bidTotalsMap = new Map<string, number>();
      
      lineItemsData?.forEach(item => {
        const current = bidTotalsMap.get(item.bid_id) || 0;
        bidTotalsMap.set(item.bid_id, current + item.total_price_cents);
      });

      // Transform to BidHistoryItem
      const transformedBids: BidHistoryItem[] = bidsData
        .filter(bid => bid.rfqs && (bid.rfqs as any).job)
        .map(bid => {
          const rfq = bid.rfqs as any;
          const job = rfq?.job;
          const org = job?.organization;
          const award = awardsMap.get(job?.id);
          const feedback = feedbackMap.get(job?.id);

          // Determine outcome
          let outcome: BidOutcome = 'pending';
          if (award) {
            outcome = award.winning_bid_id === bid.id ? 'won' : 'lost';
          } else if (job?.status === 'expired' || job?.status === 'cancelled') {
            outcome = 'expired';
          }

          // Calculate response time in hours
          const rfqCreatedAt = new Date(rfq.created_at);
          const bidCreatedAt = new Date(bid.created_at);
          const responseTimeHours = (bidCreatedAt.getTime() - rfqCreatedAt.getTime()) / (1000 * 60 * 60);

          return {
            id: bid.id,
            submittedAt: bid.created_at,
            jobId: job?.id || '',
            jobTitle: job?.title || 'Untitled Job',
            jobCategory: job?.category || 'other',
            rfqId: rfq?.id || '',
            rfqPublicId: rfq?.public_id || '',
            clientName: org?.name || 'Unknown Client',
            clientOrganizationId: job?.organization_id || '',
            totalBidCents: bidTotalsMap.get(bid.id) || 0,
            outcome,
            awardedAt: award?.awarded_at || null,
            awardAmountCents: null, // Award amount not available via restricted view
            selectionReasons: feedback?.selection_reasons || null,
            additionalComments: feedback?.additional_comments || null,
            leadTimeDays: bid.lead_time_days,
            rfqClosesAt: rfq?.closes_at || '',
            responseTimeHours: Math.max(0, responseTimeHours),
          };
        });

      setBids(transformedBids);
    } catch (err) {
      console.error('Error fetching bid history:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bid history');
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters.dateRange.start, filters.dateRange.end]);

  useEffect(() => {
    fetchBids();
  }, [fetchBids]);

  // Apply client-side filters
  const filteredBids = useMemo(() => {
    return bids.filter(bid => {
      // Outcome filter
      if (filters.outcome !== 'all' && bid.outcome !== filters.outcome) {
        return false;
      }

      // Category filter
      if (filters.category !== 'all' && bid.jobCategory !== filters.category) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesTitle = bid.jobTitle.toLowerCase().includes(searchLower);
        const matchesClient = bid.clientName.toLowerCase().includes(searchLower);
        const matchesRfqId = bid.rfqPublicId.toLowerCase().includes(searchLower);
        if (!matchesTitle && !matchesClient && !matchesRfqId) {
          return false;
        }
      }

      return true;
    });
  }, [bids, filters.outcome, filters.category, filters.search]);

  return {
    bids: filteredBids,
    loading,
    error,
    filters,
    setFilters,
    refetch: fetchBids,
  };
}
